import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { money, dt } from "@/lib/format";
import { PageHeader, Loader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Wallet, Download, Lock } from "lucide-react";
import { toast } from "sonner";

export default function MessengerSettlements() {
  const [mensajeros, setMensajeros] = useState([]);
  const [mensajero, setMensajero] = useState("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = useState(null);
  const [checked, setChecked] = useState({});
  const [motivos, setMotivos] = useState({});
  const [closing, setClosing] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/rendiciones/mensajeros").then((r) => { setMensajeros(r.data); if (r.data.length) setMensajero(r.data[0]); });
    api.get("/rendiciones").then((r) => setHistory(r.data));
  }, []);

  const loadPreview = async () => {
    if (!mensajero) return;
    const r = await api.get("/rendiciones/candidatos", { params: { mensajero, fecha } });
    setPreview(r.data);
    const ch = {};
    r.data.pedidos.forEach((o) => { ch[o.id] = true; });
    setChecked(ch);
    setMotivos({});
  };
  useEffect(() => { if (mensajero) loadPreview(); /* eslint-disable-next-line */ }, [mensajero, fecha]);

  const cerrar = async () => {
    const excluidos = preview.pedidos.filter((o) => !checked[o.id]).map((o) => ({ order_id: o.id, motivo: motivos[o.id] || "Excluido manualmente" }));
    setClosing(true);
    try {
      await api.post("/rendiciones/cerrar", { mensajero, fecha, excluidos });
      toast.success("Rendición cerrada");
      loadPreview();
      api.get("/rendiciones").then((r) => setHistory(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setClosing(false); }
  };

  const exportCsv = (id) => {
    const token = localStorage.getItem("ms_token");
    window.open(`${API}/rendiciones/${id}/export?auth=${token}`, "_blank");
  };

  const totalRegistradoManual = preview ? preview.pedidos.filter((o) => checked[o.id]).reduce((s) => s, 0) : 0;

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto">
      <PageHeader title="Rendición de mensajeros" subtitle="Cobro contra entrega, por mensajero y por día." />

      <Tabs defaultValue="hoy">
        <TabsList className="rounded-full mb-4">
          <TabsTrigger value="hoy" className="rounded-full" data-testid="tab-rendicion-hoy">Rendición del día</TabsTrigger>
          <TabsTrigger value="historial" className="rounded-full" data-testid="tab-rendicion-historial">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="hoy">
          <div className="ms-card p-4 mb-4 flex flex-wrap items-center gap-3">
            <div>
              <Label>Mensajero</Label>
              <Select value={mensajero} onValueChange={setMensajero}>
                <SelectTrigger className="w-44 rounded-xl mt-1" data-testid="select-mensajero"><SelectValue placeholder="Elegí…" /></SelectTrigger>
                <SelectContent>{mensajeros.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fecha</Label>
              <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="rounded-xl mt-1" data-testid="input-fecha-rendicion" />
            </div>
          </div>

          {!preview ? <EmptyState icon={Wallet} title="Elegí mensajero y fecha" subtitle="Vas a ver los pedidos entregados contra entrega ese día." /> : preview.pedidos.length === 0 ? (
            <EmptyState icon={Wallet} title="Sin pedidos" subtitle="Este mensajero no tiene entregas contra entrega en esa fecha." />
          ) : (
            <>
              <div className="space-y-2 mb-4">
                {preview.pedidos.map((o) => (
                  <div key={o.id} className="ms-card p-3 flex items-center gap-3" data-testid={`settlement-order-${o.id}`}>
                    <Checkbox checked={!!checked[o.id]} onCheckedChange={(v) => setChecked({ ...checked, [o.id]: v })} data-testid={`settlement-checkbox-${o.id}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">#{o.numero} · {o.cliente_nombre} · {money(o.total)}</p>
                      {!checked[o.id] && (
                        <Input placeholder="Motivo de exclusión…" className="rounded-xl h-8 text-sm mt-1"
                          value={motivos[o.id] || ""} onChange={(e) => setMotivos({ ...motivos, [o.id]: e.target.value })} data-testid={`settlement-motivo-${o.id}`} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="ms-card p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex gap-6">
                  <div><p className="text-xs text-muted-foreground">Total esperado</p><p className="font-head font-semibold text-lg">{money(preview.total_esperado)}</p></div>
                  <div><p className="text-xs text-muted-foreground">Total registrado</p><p className="font-head font-semibold text-lg text-primary">{money(preview.total_registrado)}</p></div>
                </div>
                <Button onClick={cerrar} disabled={closing} className="rounded-full bg-primary hover:bg-primary/90 gap-2" data-testid="cerrar-rendicion-button">
                  <Lock className="h-4 w-4" /> {closing ? "Cerrando…" : "Cerrar rendición"}
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="historial">
          {history.length === 0 ? <EmptyState icon={Wallet} title="Sin rendiciones cerradas" /> : (
            <div className="space-y-2">
              {history.map((r) => (
                <div key={r.id} className="ms-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`settlement-history-${r.id}`}>
                  <div>
                    <p className="font-semibold text-sm">{r.mensajero} · {r.fecha}</p>
                    <p className="text-xs text-muted-foreground">Cerrada por {r.cerrada_por} · {dt(r.created_at)} · {r.pedidos_incluidos.length} pedidos</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="rounded-full border-0 bg-emerald-50 text-emerald-600">{money(r.total_registrado)} / {money(r.total_esperado)}</Badge>
                    <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => exportCsv(r.id)} data-testid={`export-settlement-${r.id}`}><Download className="h-4 w-4" /> CSV</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
